import {
  THEME_CONTRACT_VERSION,
  TOKEN_NAMES,
  cssVariable,
  isTokenName,
  isValidTokenValue,
} from './contract';
import type { TokenName, TokenSet } from './contract';
import { DEFAULT_PRESET, PRESETS, isPresetName } from './presets';
import type { PresetName } from './presets';

/**
 * Rungs 1 and 2, resolved: a named preset, then the tenant's own overrides on
 * top, then CSS custom properties on `:root`.
 *
 * `resolveTheme` is pure and takes `unknown`, which is what makes it the piece
 * worth testing: the input is a JSONB document a tenant edited through an admin
 * console, and every wrong thing it can contain — an unknown token, a colour
 * that is not a colour, a preset from a future release, a `contract_version`
 * this bundle has never heard of — has to end in a painted page and a line in
 * the console, never in a stack trace on somebody's booking site.
 *
 * The rule, stated once: **ignore what you do not understand, keep what you
 * had, say so out loud.**
 */

export interface ResolvedTheme {
  preset: PresetName;
  tokens: TokenSet;
  /** Every ignored thing, in order. Returned as well as logged, so it is testable. */
  warnings: string[];
}

/**
 * Keys that are structure rather than tokens, so they never warn as unknown.
 *
 * There is deliberately **no alias table** beside this set. An earlier revision
 * carried one — `sand → serene`, plus top-level shorthands (`accent`, `logo`,
 * `hero`) for tokens that already have contract names — and it existed for
 * exactly one row: `apps/guest-api`'s test fixture, written before the contract
 * was. The fixture has been rewritten in contract form, so the table went with
 * it. Keeping compatibility code alive for a fixture would have made the
 * contract two vocabularies wide, and the version this bundle speaks is
 * `THEME_CONTRACT_VERSION` and nothing else.
 *
 * A real tenant row carrying the old shape is an operator `UPDATE`, not a
 * branch here: it renders the default preset with one console warning naming
 * the key, which is the same failure mode as any unknown token and is
 * recoverable by a person reading the page.
 */
const STRUCTURAL_KEYS = new Set([
  'contract_version',
  'preset',
  'tokens',
  'sections',
  'site_title',
  'tagline',
]);

export function resolveTheme(branding: unknown): ResolvedTheme {
  const warnings: string[] = [];
  const config = isRecord(branding) ? branding : {};

  if (!isRecord(branding) && branding !== undefined && branding !== null) {
    warnings.push('branding is not an object; the default preset was used.');
  }

  /* -------- the version ------------------------------------------- */
  const declared = config['contract_version'];
  if (declared !== undefined && declared !== THEME_CONTRACT_VERSION) {
    // Not fatal, in either direction. A *lower* version is a row nobody has
    // re-saved since the last bump and the one version of backward
    // compatibility §6.4.3 promises covers it; a *higher* one is a console that
    // has shipped ahead of this bundle, and the tokens it does not know will
    // fall out one by one below with their own warnings.
    warnings.push(
      `branding declares theme contract v${String(declared)}; this bundle speaks v${String(
        THEME_CONTRACT_VERSION,
      )}. Unknown values will be ignored.`,
    );
  }

  /* -------- rung 1: the preset ------------------------------------ */
  const rawPreset = config['preset'];
  let preset: PresetName = DEFAULT_PRESET;
  if (typeof rawPreset === 'string' && rawPreset !== '') {
    if (isPresetName(rawPreset)) {
      preset = rawPreset;
    } else {
      warnings.push(`unknown preset "${rawPreset}"; using "${DEFAULT_PRESET}".`);
    }
  } else if (rawPreset !== undefined) {
    warnings.push('preset must be a string; using the default.');
  }

  const tokens: TokenSet = { ...PRESETS[preset] };

  /* -------- rung 2: token overrides ------------------------------- */
  const overrides = collectOverrides(config, warnings);
  for (const [name, value] of overrides) {
    if (!isValidTokenValue(name, value)) {
      warnings.push(`token "${name}" has an unusable value; the preset's was kept.`);
      continue;
    }
    tokens[name] = value;
  }

  for (const warning of warnings) console.warn(`[theme] ${warning}`);

  return { preset, tokens, warnings };
}

/**
 * Gather `branding.tokens`, dropping — loudly — anything the contract does not
 * name. Tokens live under `tokens` and nowhere else: there is one place to look
 * for an override, and a key at the top level is a key, not a token.
 */
function collectOverrides(
  config: Record<string, unknown>,
  warnings: string[],
): [TokenName, string][] {
  const found: [TokenName, string][] = [];

  const raw = config['tokens'];
  if (raw !== undefined) {
    if (!isRecord(raw)) {
      warnings.push('branding.tokens is not an object; it was ignored.');
    } else {
      for (const [key, value] of Object.entries(raw)) {
        if (!isTokenName(key)) {
          // The expected traffic of a bundle one release behind the console.
          warnings.push(`unknown token "${key}" ignored (theme contract v1).`);
          continue;
        }
        const text = asText(value);
        if (text === null) {
          warnings.push(`token "${key}" must be a string or a number; it was ignored.`);
          continue;
        }
        found.push([key, text]);
      }
    }
  }

  for (const key of Object.keys(config)) {
    if (!STRUCTURAL_KEYS.has(key)) {
      warnings.push(`unknown branding key "${key}" ignored (theme contract v1).`);
    }
  }

  return found;
}

/**
 * Paint the resolved tokens onto an element — `document.documentElement` in the
 * app, a detached node in a test.
 *
 * Empty values are skipped rather than written: `image.logo` and `image.hero`
 * are empty in every preset (a stock photograph on a real spa's page would be
 * worse than none), and writing an empty custom property would make
 * `var(--ys-image-hero, …)` skip its fallback.
 */
export function applyTheme(root: HTMLElement, theme: ResolvedTheme): void {
  for (const name of TOKEN_NAMES) {
    const value = theme.tokens[name];
    const property = cssVariable(name);
    if (value === '') root.style.removeProperty(property);
    else root.style.setProperty(property, value);
  }
  root.dataset['theme'] = theme.preset;
}

/**
 * The one place a token becomes a CSS value in a component: `url("…")` for the
 * two image tokens, and `null` when there is no image, so a caller writes
 * `style={{ backgroundImage: imageValue(tokens, 'image.hero') ?? undefined }}`
 * rather than assembling a string.
 *
 * The value has already passed `isValidTokenValue`, which admits only `https:`
 * and `data:image/…` — so this cannot become `url(javascript:…)` however the row
 * was authored.
 */
export function imageValue(tokens: TokenSet, name: 'image.logo' | 'image.hero'): string | null {
  const value = tokens[name];
  return value === '' ? null : `url("${value}")`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asText(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

export { THEME_CONTRACT_VERSION };
export type { TokenSet, TokenName, PresetName };
